/**
 * Wraps a video and in-snippet play/pause + mute controls.
 * Upgrades via <x-video-custom-controls> in the media snippet.
 */

function syncUi (root: HTMLElement, video: HTMLVideoElement): void {
  const paused = video.paused
  root.querySelectorAll<HTMLElement>('[data-video-play-pause] [data-show-when]').forEach((el) => {
    const when = el.getAttribute('data-show-when')
    const show = when === 'paused' ? paused : when === 'playing' ? !paused : false
    el.classList.toggle('hidden', !show)
  })

  const muted = video.muted
  root.querySelectorAll<HTMLElement>('[data-video-mute] [data-show-when]').forEach((el) => {
    const when = el.getAttribute('data-show-when')
    const show = when === 'muted' ? muted : when === 'unmuted' ? !muted : false
    el.classList.toggle('hidden', !show)
  })

  const playPause = root.querySelector<HTMLButtonElement>('[data-video-play-pause]')
  const muteBtn = root.querySelector<HTMLButtonElement>('[data-video-mute]')
  if (playPause) {
    playPause.setAttribute('aria-label', paused ? 'Play video' : 'Pause video')
  }
  if (muteBtn) {
    muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video')
  }
}

export class VideoCustomControlsElement extends HTMLElement {
  private video: HTMLVideoElement | null = null

  private readonly onVideoState = (): void => {
    if (this.video) {
      syncUi(this, this.video)
    }
  }

  private readonly handleClick = (e: Event): void => {
    const t = e.target as HTMLElement
    const playPause = t.closest<HTMLButtonElement>('[data-video-play-pause]')
    const muteBtn = t.closest<HTMLButtonElement>('[data-video-mute]')
    if (!playPause && !muteBtn) return

    const video = this.video
    if (!video) return

    e.preventDefault()

    if (playPause) {
      if (video.paused) {
        video.play().catch(() => undefined)
      } else {
        video.pause()
      }
    } else if (muteBtn) {
      video.muted = !video.muted
    }
    syncUi(this, video)
  }

  connectedCallback (): void {
    this.video = this.querySelector('video')
    if (!this.video) return

    this.video.addEventListener('play', this.onVideoState)
    this.video.addEventListener('pause', this.onVideoState)
    this.video.addEventListener('volumechange', this.onVideoState)
    this.addEventListener('click', this.handleClick)
    this.onVideoState()
  }

  disconnectedCallback (): void {
    if (this.video) {
      this.video.removeEventListener('play', this.onVideoState)
      this.video.removeEventListener('pause', this.onVideoState)
      this.video.removeEventListener('volumechange', this.onVideoState)
      this.video = null
    }
    this.removeEventListener('click', this.handleClick)
  }
}
